/**
 * ヘッダーコンポーネント
 *
 * アプリケーションのグローバルヘッダー
 * ロゴ、ナビゲーション、ユーザーメニューを含む
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useT } from '../../i18n';

function Header() {
  const { user, logout } = useAuth();
  const { userData, isPremium } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { t } = useT();

  // ナビゲーションリンク
  const navLinks = [
    { path: '/dashboard', label: t('nav.dashboard') },
    { path: '/pages', label: t('nav.pages') },
    { path: '/subscription', label: t('nav.subscription') || 'Subscription' },
    { path: '/settings', label: t('nav.settings') }
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* ロゴ */}
        <Link to="/dashboard" className="flex items-center gap-3">
          <img
            src="/knovelo-trans-icon.png"
            alt="KnoVelo"
            className="w-10 h-10 rounded-xl"
          />
          <div>
            <h1 className="text-xl font-bold text-gray-900">KnoVelo</h1>
            <p className="text-xs text-gray-500 hidden sm:block">{t('header.tagline')}</p>
          </div>
        </Link>

        {/* ナビゲーション（デスクトップ） */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${location.pathname === link.path
                  ? 'bg-primary/10 text-primary'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* 右側: ユーザーメニュー */}
        <div className="flex items-center gap-4">
          {/* プランバッジ */}
          {isPremium === true ? (
            <span className="hidden sm:inline-flex items-center px-3 py-1 text-xs font-semibold bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full">
              Premium
            </span>
          ) : (
            <Link
              to="/subscription"
              className="hidden sm:inline-flex items-center px-3 py-1 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
            >
              {t('nav.freeUpgrade')}
            </Link>
          )}

          {/* ユーザーアバター */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                  {user?.displayName?.charAt(0) || 'U'}
                </div>
              )}
            </button>

            {/* ドロップダウンメニュー */}
            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-20 py-2">
                  {/* ユーザー情報 */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="font-medium text-gray-900">{user?.displayName}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>

                  {/* プラン情報 */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">{t('header.currentPlan')}</p>
                    <p className="font-medium text-gray-900">
                      {isPremium ? 'Premium' : 'Free'}
                    </p>
                    {!isPremium && (
                      <Link
                        to="/subscription"
                        className="text-xs text-primary hover:underline"
                        onClick={() => setShowUserMenu(false)}
                      >
                        {t('header.premiumUpgrade')}
                      </Link>
                    )}
                  </div>

                  {/* メニューリンク（モバイル用） */}
                  <div className="md:hidden py-2 border-b border-gray-100">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>

                  {/* ログアウト */}
                  <button
                    onClick={() => {
                      logout();
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
